import React, {
  Dispatch,
  MouseEventHandler,
  SetStateAction,
  useMemo,
  useState,
} from 'react';
import { ICmsUser } from '@/types/Content/User';
import { Image as ImageType } from '@/types/Content/Image';
import { Button } from '@/components/Button/Button';
import Avatars from '@/components/Avatars';
import { Box, Divider, Stack, useMediaQuery, useTheme } from '@mui/material';
import Icon from '@/components/icons/Icon';
import Link from 'next/link';
import { PostDiscussionMagnet } from '@/components/PostDiscussionMagnet/PostDiscussionMagnet';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { AuthStore } from '@/stores/index';
import { SaveHandlerArgTypes } from '@/components/PostDiscussionMagnet/PostDiscussionModal/PostDiscussion.types';
import discoverService from '@/services/discoverService';
import discussionService from '@/services/discussionService';
import { DISCUSSIONS_PATH } from '@/common/constants/routes';
import { useRouter } from 'next/router';
import { FilterButton } from '@/components/AppPageLayout/CommunityPageLayout/AsideContent/AsideContent.styles';
import { FilterValues } from '../../constants/FilterValues';
import { SortValues } from '../../constants/SortValues';
import { GroupSubtitle, GroupTitle, Wrapper } from './GroupBanner.styles';
import { EFilters } from '../FilterSection/FilterSection';

interface GroupBannerProps {
  title: string;
  image: ImageType;
  subtitle: string;
  isMember: boolean;
  isLoading: boolean;
  users?: Array<ICmsUser>;
  onJoinLeave: MouseEventHandler<HTMLButtonElement>;
  group: any;
  slug: string;
  filteredBy: FilterValues[];
  sortedBy: any;
  setGroup: Dispatch<any>;
  children: React.ReactNode;
  filtersOpenedMob: EFilters;
  setFiltersOpenedMob: Dispatch<SetStateAction<EFilters | null>>;
}

const GroupBannerPure: React.FC<GroupBannerProps> = ({
  users,
  title,
  image,
  isMember,
  subtitle,
  isLoading,
  onJoinLeave,
  group,
  slug,
  filteredBy,
  sortedBy,
  setGroup,
  children,
  filtersOpenedMob,
  setFiltersOpenedMob,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(`${theme.breakpoints.up('md')}`);
  const { cmsUser } = AuthStore;
  const router = useRouter();

  const [loadingPost, setLoadingPost] = useState(false);

  function parseUsersToAvatars() {
    return users?.map((currentUser) => ({
      id: currentUser?.id,
      name: currentUser.firstName,
      image: currentUser?.image,
    }));
  }

  async function handleSave({
    selectedOption,
    userId,
    ...params
  }: SaveHandlerArgTypes) {
    setLoadingPost(true);
    const data = {
      userId,
      groupId: selectedOption,
      ...params,
    };
    const isMemberOnPosting = cmsUser.subscriptionGroups.some(
      (groupToCheck) => groupToCheck.id === selectedOption,
    );

    if (!isMemberOnPosting) {
      await discoverService.joinGroup(data);
      await AuthStore.getCmsUser();
      const newContent = await discoverService.getGroup(
        slug,
        undefined,
        filteredBy,
        sortedBy === SortValues.TRENDING,
      );
      setGroup(newContent);
    }

    const { discussion } = await discussionService.create(data);

    if (discussion?.slug) {
      router.push(`${DISCUSSIONS_PATH}/${discussion.slug}`);
    }
    setLoadingPost(false);
  }

  const groupOptions = useMemo(() => {
    const groups = cmsUser?.subscriptionGroups?.map((subscriptionGroup) => ({
      value: subscriptionGroup.id,
      label: subscriptionGroup.name,
    }));
    const isSubscribed = groups?.some(
      (subscriptionGroup) => subscriptionGroup.value === group?.groupId,
    );
    if (!isSubscribed) {
      groups?.push({ value: group?.groupId, label: group?.name });
    }
    return groups;
  }, [cmsUser, group]);

  const renderFilterButton = (iconName: string, key: EFilters) => {
    const opened = filtersOpenedMob === key;
    return (
      <FilterButton
        onClick={() =>
          setFiltersOpenedMob((prev) => (prev === key ? null : key))
        }
        variant={opened ? 'contained' : 'text'}
        active={opened}
      >
        <Icon iconName={iconName} />
      </FilterButton>
    );
  };

  const goBackButton = (
    <Link href="/discover">
      <Icon
        iconName="arrow-left-circle"
        style={{ fontSize: theme.spacing(11), cursor: 'pointer' }}
      />
    </Link>
  );

  return (
    <Wrapper>
      <Stack
        direction="row"
        alignItems="flex-start"
        padding={theme.spacing(6)}
        spacing={theme.spacing(4)}
      >
        {isDesktop && goBackButton}

        <Stack flex={1} gap={isDesktop ? 0 : theme.spacing(4)}>
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={theme.spacing(4)}
          >
            {!isDesktop && goBackButton}
            <GroupTitle>{title}</GroupTitle>
          </Stack>
          <GroupSubtitle>{subtitle}</GroupSubtitle>

          {!isDesktop && <Divider sx={{ borderColor: '#B7DEDE' }} />}
          <Stack
            marginTop={isDesktop ? theme.spacing(4.5) : 0}
            flexWrap="wrap"
            direction={{ xs: 'column', md: 'row' }}
            justifyContent={{ xs: 'flex-start', md: 'space-between' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            gap={theme.spacing(4)}
          >
            <Avatars
              users={parseUsersToAvatars()}
              size={{ desktop: 8.5, mobile: 8.5 }}
            />
            <Stack
              direction="row"
              width={isDesktop ? 'unset' : '100%'}
              gap={theme.spacing(1)}
            >
              <Button
                loading={isLoading}
                onClick={onJoinLeave}
                variant={isMember ? 'outlined' : 'contained'}
              >
                {isMember ? 'Leave' : 'Join'} group
              </Button>

              {!isDesktop && (
                <>
                  <Box flex={1} />
                  {renderFilterButton('sorting', EFilters.sort)}
                  {renderFilterButton('filter', EFilters.filter)}
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {isDesktop && (
        <>
          <Divider sx={{ borderColor: '#B7DEDE' }} />
          <PostDiscussionMagnet
            user={toJS(cmsUser)}
            loading={loadingPost}
            modalProps={{
              onSave: handleSave,
              titleInputProps: {
                maxLength: 60,
                placeholder: 'Discussion subject',
              },
              textEditorProps: {
                placeholder:
                  'Post a question or share your thoughts to connect with others...',
              },
              selectProps: {
                placeholder: 'Select a group',
                options: groupOptions,
                initialValue: group?.groupId,
              },
            }}
            magnetProps={{
              placeholder:
                'Post a question or share your thoughts to connect with others...',
              buttonVariant: 'inputAlike',
            }}
            bgColor="unset"
            newUI
          />
        </>
      )}

      {children}
    </Wrapper>
  );
};

export const GroupBanner = observer(GroupBannerPure);
